package ru.it_mozaika.sender.js.application.components.subscribers

import io.ktor.client.call.*
import ru.it_mozaika.sender.common.EMAIL_REGEX
import js.core.ReadonlyRecord
import js.core.jso
import kotlinx.datetime.Clock
import mui.material.*
import mui.material.SortDirection
import mui.system.responsive
import mui.system.sx
import react.*
import react.dom.aria.ariaLabel
import react.dom.aria.ariaLabelledBy
import ru.it_mozaika.components.useSnackbar
import ru.it_mozaika.sender.common.PHONE_REGEX
import ru.it_mozaika.sender.common.models.*
import ru.it_mozaika.sender.common.models.Property
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.utils.checkResponseError
import ru.it_mozaika.sender.js.application.components.SubscriptionListSelect
import ru.it_mozaika.sender.js.application.context.BackdropContext
import ru.it_mozaika.sender.js.application.context.RequestResultContext
import ru.it_mozaika.sender.js.application.context.useSubscriptionLists
import ru.it_mozaika.sender.js.application.localToInstant

import tanstack.react.table.renderCell
import tanstack.react.table.renderHeader
import tanstack.react.table.useReactTable
import tanstack.react.virtual.UseVirtualizerOptions
import tanstack.react.virtual.useVirtualizer
import tanstack.table.core.*
import web.cssom.*
import web.html.HTMLDivElement


external interface FileProcessingDialogProps: Props {
    var data: List<Map<String, String>>
    var isOpen: Boolean
    var onClose: () -> Unit
    var store: Store
    var subscriptions: List<String>
}

val fileProcessingDialog = FC<FileProcessingDialogProps> { props ->
    val (properties, setProperties) = useState(
        listOf(
            Property(id = "Email", store = props.store.id, name = "Email", variable = "Email"),
            Property(id = "Phone", store = props.store.id, name = "Телефон", variable = "Phone")
        )
    )
    var (columnVisibility, setColumnVisibility) = useState<ReadonlyRecord<String, Boolean>>(jso {})
    val (columnOrder, setColumnOrder) = useState<ColumnOrderState>(jso {})
    val requestResponse = useContext(RequestResultContext)
    val columns = useMemo(props.data) { props.data.first().map { it.key } }
    val parentRef = useRef<HTMLDivElement>()
    val (emailVerified, setEmailVerified) = useState(false)
    val (requestWithErrors, setRequestWithErrors) = useState<List<SubscriberData>>(listOf())
    val (errorsList, setErrorsList) = useState<List<String>>(listOf())
    val (showConfirmDialog, setShowConfirmDialog) = useState(false)
    val showNotification = useSnackbar().enqueueSnackbar
    val backdrop = useContext(BackdropContext)
    var showAddListDialog by useState(false)
    val virtualList = useVirtualizer(
        jso<UseVirtualizerOptions<HTMLDivElement, HTMLDivElement>> {
            this.getScrollElement = useCallback(parentRef.current) { parentRef.current }
            this.estimateSize = { 5 }
            overscan = 15
            this.count = props.data.size
        }
    )
    val (columnIdentifier, setColumnIdentifier) = useState<Map<String, Property>>(mapOf())
    val (emailFieldIsIndicated, setEmailFieldIsIndicated) = useState(false)
    val (propertiesInUse, setPropertiesInUse) = useState<List<Property>>(listOf())
    fun addSubscriber(request: List<SubscriberData>) {
        backdrop.show()
        Http.post(
            path = "/subscribers/add",
            body = request,
        ) { response ->
            requestResponse.handleRequestRespond(response)
            backdrop.hide()
        }
    }

    fun loadProperties() {
        Http.get("/subscribers/properties", mapOf("store" to props.store.id)) { response ->
            response.checkResponseError()?.let {
                showNotification(it, jso { variant = AlertColor.error })
                return@get
            }
            val newList = response.body<List<Property>>()
            setProperties {
                it.plus(newList)
            }
        }
    }
    useEffect(columnIdentifier, props.data) {
        val isIndicated = columnIdentifier.filterValues { it.id == "Email" }.isNotEmpty()
        setEmailFieldIsIndicated(isIndicated)
        setPropertiesInUse {
            properties.filter { property ->
                property in columnIdentifier.map { it.value }
            }
        }
    }
    useEffect(properties, props.data) {
        val newMap = mutableMapOf<String, Property>()
        columns.forEach { column ->
            val property = properties.find { it.name.lowercase() == column.lowercase() }
            if (property != null) {
                newMap[column] = property
            }
        }
        setColumnIdentifier(newMap)
    }
    useEffect(props.store) {
        loadProperties()
    }
    val handleOnRequest = {
        val errors = mutableListOf<String>()
        val subscriptions =
            props.subscriptions.ifEmpty { listOf() }
        val request = props.data.mapIndexedNotNull { index, map ->
            val emailKey = columnIdentifier.filterValues { it.id == "Email" }.keys.first()
            val phoneKey = columnIdentifier.filterValues { it.id == "Phone" }
            val phone = if (phoneKey.isNotEmpty()) map[phoneKey.keys.first()] else null
            val email = map[emailKey]
            if (map[emailKey] != null) {
                if (!email?.matches(EMAIL_REGEX)!!) {
                    errors += "Некорректно заполнен Email - $email в строке ${index + 2}."
                    return@mapIndexedNotNull null
                }
                val propertiesList = map.entries.mapNotNull { (header, value) ->
                    if (value.isNotEmpty() && value.isNotBlank()) {
                        val property = columnIdentifier[header]
                        if (property?.id == "Phone") {
                            if (!value.matches(PHONE_REGEX)) {
                                errors += "Некорректно заполнен Телефон $phone в строке ${index + 2}"
                            }
                        }
                        if (property != null && property.id != "Email" && property.id != "Phone") {
                            try {
                                when (property.type) {
                                    PropertyType.String -> SubscriberDataProperty.StringProperty(
                                        propertyId = property.id,
                                        values = listOf(value)
                                    )

                                    PropertyType.Int -> SubscriberDataProperty.IntProperty(
                                        propertyId = property.id,
                                        values = listOf(value.toInt())
                                    )

                                    PropertyType.Float -> SubscriberDataProperty.FloatProperty(
                                        propertyId = property.id,
                                        values = listOf(value.toFloat())
                                    )

                                    PropertyType.Date -> SubscriberDataProperty.DateProperty(
                                        propertyId = property.id,
                                        values = listOf(value.localToInstant().also { println("localToInstant - $it") })
                                    )

                                    PropertyType.Bool -> SubscriberDataProperty.BoolProperty(
                                        propertyId = property.id,
                                        values = listOf(value.toBooleanStrict())
                                    )
                                }
                            } catch (e: Exception) {
                                errors += "Указано некорректное значение \"$value\" в поле \"$header\" в строке ${index + 2}"
                                null
                            }
                        } else {
                            null
                        }
                    } else {
                        null
                    }
                }

                SubscriberData(
                    id = "",
                    email = map[emailKey] ?: throw (NullPointerException()),
                    store = props.store.id,
                    subscriptions = subscriptions,
                    emailVerified = emailVerified,
                    properties = propertiesList.filter { it.values.isNotEmpty() },
                    regDate = Clock.System.now(),
                    phone = phone

                )
            } else {
                errors += "Не удалось обнаружить поле Email"
                null
            }
        }
        if (request.isNotEmpty() && errors.isEmpty()) {

            addSubscriber(request)
            props.onClose()
        } else {
            setRequestWithErrors(request)
            setErrorsList(errors)
            setShowConfirmDialog(true)
        }
    }
    val table = useReactTable<Map<String, String>>(
        options = jso {
            this.columns = useMemo(columns) {
                columns.map { column ->
                    jso<ColumnDef<Map<String, String>, Any>> {
                        this.accessorFn = { entry, _ ->
                            entry[column]?.let {
                                if (it.isNotBlank()) it else "---"
                            } ?: "---"
                        }
                        cell = ColumnDefTemplate { context ->
                            Box.create {
                                sx {
                                    minHeight = 50.px
                                    minWidth = 100.px
                                }
                                +context.getValue().toString()
                            }
                        }
                        id = column
                        header = StringOrTemplateHeader(column)
                    }
                }
            }.toTypedArray()
            data = useMemo(props.data) { props.data.toTypedArray() }
            this.state = jso {
                this.columnVisibility = columnVisibility
                this.columnOrder = columnOrder
            }
            this.onColumnVisibilityChange = {
                columnVisibility = it.unsafeCast<ReadonlyRecord<String, Boolean>>()
                setColumnVisibility(it.unsafeCast<ReadonlyRecord<String, Boolean>>())
            }
            this.onColumnOrderChange = {
                setColumnOrder(it.unsafeCast<Array<String>>())
            }
            this.getCoreRowModel = getCoreRowModel()
            this.debugTable = true
            this.debugHeaders = true
            this.debugColumns = true
        }
    )
    Dialog {
        sx {
            width = 100.pct
            marginTop = 56.px
        }
        asDynamic().maxWidth = "xl"
        DialogTitle {
            +"Обработка файла"
        }
        open = props.isOpen
        DialogContent {
            if (!emailFieldIsIndicated) {
                Stack {
                    sx {
                        alignItems = AlignItems.center
                    }
                    direction = responsive(StackDirection.row)
                    spacing = responsive(2)
                    mui.icons.material.ErrorOutline {
                        sx {
                            paddingBottom = 5.px
                        }
                        color = SvgIconColor.error
                        fontSize = SvgIconSize.large
                        this.titleAccess = "Не указан обязательный атрибут Email"
                    }
                    InputLabel {
                        +"Не указан обязательный атрибут Email"
                    }
                }
            }
            TableContainer {
                ref = parentRef
                sx {
                    marginRight = 5.px
                    minWidth = 500.px
                    maxHeight = 100.vh - 70.px
                }
                Table {
                    ariaLabel = "file-processing-table"
                    ariaLabelledBy = "file-processing-table-title"
                    stickyHeader = true
                    TableHead {
                        table.getHeaderGroups().forEach { headerGroup ->
                            headerGroup.headers.forEach { header ->
                                val headerName = renderHeader(header).toString()
                                TableCell {
                                    key = header.id
                                    align = TableCellAlign.center
                                    padding = TableCellPadding.normal
                                    sortDirection = SortDirection.asc
                                    FormControl {
                                        sx {
                                            minWidth = 100.px
                                        }
                                        fullWidth = true
                                        InputLabel {
                                            +headerName
                                        }
                                        Select {
                                            color = InputBaseColor.secondary
                                            val selectedField = useMemo(
                                                columnIdentifier,
                                                headerName,
                                                properties
                                            ) { columnIdentifier[headerName] }
                                            println("selectedField - $selectedField")
                                            this.id = "property-id-select"
                                            labelId = "property-id-select-label"
                                            variant = SelectVariant.outlined
                                            this.error = selectedField == null
                                            this.label = ReactNode(selectedField?.name ?: "---")
                                            this.value = selectedField?.id ?: ""
                                            renderValue = {
                                                backdrop.show()
                                                ReactNode(selectedField?.name ?: "")
                                                    .also { backdrop.hide() }
                                            }
                                            onChange = { event, _ ->
                                                val propertyId = event.target.value
                                                val selectedProperty = properties.find { it.id == propertyId }
                                                if (selectedProperty != null) {
                                                    setColumnIdentifier { current ->
                                                        val newValue =
                                                            current.plus(mapOf(headerName to selectedProperty))
                                                        newValue
                                                    }
                                                }
                                            }
                                            if (selectedField != null) {
                                                this.startAdornment = mui.icons.material.ClearSharp.create {
                                                    onClick = {
                                                        setColumnIdentifier {
                                                            it.minus(headerName)
                                                        }
                                                    }
                                                }
                                            }
                                            properties.forEach { property ->
                                                if (!propertiesInUse.contains(property)) {
                                                    MenuItem {
                                                        key = property.id
                                                        value = property.id
                                                        ListItemText {
                                                            primary = ReactNode(property.name)
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    TableBody {
                        virtualList.getVirtualItems().forEach { item ->
                            val row = table.getRowModel().rows[item.index]
                            val labelId = "file-processing-table-row-${item.index}"
                            TableRow {
                                tabIndex = -1
                                sx {
                                    cursor = Cursor.pointer
                                }
                                hover = true
                                row.getVisibleCells().forEachIndexed { index, cell ->
                                    TableCell {
                                        id = labelId
                                        scope = "row"
                                        padding = TableCellPadding.none
                                        align = TableCellAlign.center
                                        +renderCell(cell)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        DialogActions {
            sx {
                alignItems = AlignItems.flexEnd
            }
            Stack {
                direction = responsive(StackDirection.row)
                spacing = responsive(2)
                FormControlLabel {
                    control = Checkbox.create {
                        color = CheckboxColor.secondary
                        onChange = { _, _ ->
                            setEmailVerified { current ->
                                !current
                            }
                        }
                        checked = emailVerified
                    }
                    label = ReactNode("Email верифицирован")
                }
                Button {
                    color = ButtonColor.secondary
                    disabled = !emailFieldIsIndicated
                    +"Импортировать"
                    onClick = {
                        handleOnRequest()
                    }
                    variant = ButtonVariant.contained
                }
                Button {
                    color = ButtonColor.secondary
                    disabled = !emailFieldIsIndicated
                    +"Выбрать списки"
                    onClick = {
                        showAddListDialog = true
                    }
                    variant = ButtonVariant.outlined
                }
                Button {
                    variant = ButtonVariant.outlined
                    +"Отмена"
                    color = ButtonColor.error
                    onClick = {
                        props.onClose()
                    }
                }
            }
        }
        confirmDialog {
            this.onClose = {
                setShowConfirmDialog(false)
            }
            this.originalCount = props.data.size
            this.errors = errorsList
            this.requestWithErrors = requestWithErrors
            this.isOpen = showConfirmDialog
            this.onDownloadClick = {
                backdrop.show()
                addSubscriber(requestWithErrors)
                setShowConfirmDialog(false)
                props.onClose()
            }
        }
        AddListsDialog {
            this.onImportHandler = {
                handleOnRequest()
            }
            this.onClose = {
                showAddListDialog = false
            }
            isOpen = showAddListDialog
        }
    }
}

external interface AddListsDialogProps: Props{
    var isOpen: Boolean
    var onClose: () -> Unit
    var onImportHandler: () -> Unit
}
val AddListsDialog = FC<AddListsDialogProps> { props ->
    val subContext = useSubscriptionLists()
    Dialog {
        open = props.isOpen
        DialogTitle {
            +"Выберите подписки и тэги:"
        }
        DialogContent {
            Stack {
                sx {
                    padding = 5.px
                }
                direction = responsive(StackDirection.column)
                spacing = responsive(2)
                SubscriptionListSelect ()
                Typography {
                    +"Тэги"
                }
                Divider()
            }
        }
        DialogActions {
            sx {
                alignItems = AlignItems.end
            }
            Stack {
                direction = responsive(StackDirection.row)
                spacing = responsive(2)
                Button {
                    color = ButtonColor.secondary
                    +"Импортировать"
                    onClick = {
                        props.onImportHandler()
                    }
                    variant = ButtonVariant.outlined
                }
                Button {
                    variant = ButtonVariant.outlined
                    +"Назад"
                    color = ButtonColor.error
                    onClick = {
                        subContext.clearCurrentIds()
                        props.onClose()
                    }
                }
            }
        }
    }
}